import type { RecommendationData } from '../../../shared/services/aiService';
import type { StreamEvent } from '../types/chat';
import { fixOrderedListFormat } from '../../../shared/utils/textFormatter';

export interface StreamMessageState {
  rawText: string;
  text: string;
  done: boolean;
  error?: string;
  recommendation?: RecommendationData;
}

export const createStreamMessageState = (initialText: string = ''): StreamMessageState => ({
  rawText: initialText,
  text: initialText,
  done: false,
});

// 1.6.0: streaming 和 final 统一使用 fixOrderedListFormat
// fixOrderedListFormat 仅做"合并拆行序号 + 保持原始编号"这一基础能力
// 其余 Markdown 渲染（加粗、列表编号等）完全交给 XMarkdown（v1.3.44 引入）
export const formatStreamingText = (text: string): string => fixOrderedListFormat(text);
export const formatFinalText = (text: string): string => fixOrderedListFormat(text);

export const applyChunkWithRollback = (
  state: StreamMessageState,
  chunk: string,
  options?: { shouldRollback?: boolean; rollbackText?: string }
): StreamMessageState => {
  if (options?.shouldRollback && options.rollbackText !== undefined) {
    const rawText = options.rollbackText;
    return {
      ...state,
      rawText,
      text: formatStreamingText(rawText),
    };
  }

  return applyStreamEvent(state, {
    type: 'chunk',
    payload: { text: chunk },
  });
};

export const applyStreamEvent = (
  state: StreamMessageState,
  event: StreamEvent
): StreamMessageState => {
  switch (event.type) {
    case 'chunk': {
      const rawText = state.rawText + event.payload.text;
      return {
        ...state,
        rawText,
        text: formatStreamingText(rawText),
      };
    }
    case 'done':
      return {
        ...state,
        done: true,
        text: formatFinalText(state.rawText),
      };
    case 'error':
      return {
        ...state,
        error: event.payload.message,
      };
    case 'recommendation':
      return {
        ...state,
        recommendation: event.payload,
      };
    // 1.6.0: progress 事件不影响消息文本状态，由 ChatInterface 单独处理
    case 'progress':
      return state;
    default:
      return state;
  }
};
